// @flow

import { User } from 'lucide-react';

import { View } from 'reshaped';

import { Icon } from '@gandi/reshaped';
import { useTranslator } from '@gandi/translate';

import type { Contact, Role } from 'src/Cart/ContactForm/types';

import { ContactFormModal } from '../Modal/Content/ContactFormModal';
import { InvoiceSelectModal } from '../Modal/Content/InvoiceSelectModal';
import { OwnerSelectModal } from '../Modal/Content/OwnerSelectModal';
import { ResellerOwnerSelectModal } from '../Modal/Content/ResellerOwnerSelectModal';

export type ModalType =
  | 'ownerSelectModal'
  | 'invoiceSelectModal'
  | 'createAccountModal'
  | 'editAccountModal'
  | 'customerSelectModal';

export type OpenModalParams = {
  type: ModalType,
  orgId?: string,
  replace?: boolean,
  actionOnSuccess?: 'onClose' | 'onBack',
  currentRole?: Role,
};

export type OpenModalFunction = OpenModalParams => void;

export type Props = {
  onClose: () => void,
  /** Only defined if there is a previous view in the stack */
  onBack?: () => void,
  /** Pushes a new view onto the stack */
  openModal: OpenModalFunction,
  /** The organization or customer resolved from the ID in the current stack entry */
  contact?: Contact,
  setTabs: (tab: 'customers' | 'organizations') => void,
  tabs: 'customers' | 'organizations',
  actionOnSuccess?: 'onClose' | 'onBack',
  currentRadioValue: ?string,
  setCurrentRadioValue: React.ReactSetStateFunction<?string>,
  role: Role,
  initialTotalCount?: number,
};

type Component = Props => React.MixedElement;

type ModalComponentsMap = {
  ownerSelectModal: Component,
  invoiceSelectModal: Component,
  createAccountModal: Component,
  editAccountModal: Component,
  customerSelectModal: Component,
};

const MODAL_COMPONENTS: ModalComponentsMap = {
  ownerSelectModal: OwnerSelectModal,
  invoiceSelectModal: InvoiceSelectModal,
  createAccountModal: ContactFormModal,
  editAccountModal: ContactFormModal,
  customerSelectModal: ResellerOwnerSelectModal,
};

function Decorator({ children }: { children: React.Node }) {
  return (
    <View gap={4} direction="row">
      <View
        borderRadius="medium"
        backgroundColor="primary"
        height="28px"
        width="28px"
        align="center"
        justify="center"
      >
        <Icon svg={User} size={4} />
      </View>
      {children}
    </View>
  );
}

export function useModalType(type?: ModalType): {
  modalTitle: React.Node,
  Modal: Component,
} {
  const { __ } = useTranslator();
  const currentType = type || 'invoiceSelectModal';
  const ModalComponent = MODAL_COMPONENTS[currentType];
  const ownerSelectModalTitle = __('Choose your owner info');

  const modalTitle = {
    ownerSelectModal: ownerSelectModalTitle,
    invoiceSelectModal: __('Choose your invoice info'),
    createAccountModal: <Decorator>{__('New contact')}</Decorator>,
    editAccountModal: <Decorator>{__('Edit contact')}</Decorator>,
    customerSelectModal: ownerSelectModalTitle,
  }[currentType];

  return { modalTitle, Modal: ModalComponent };
}
